import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import UploadZone from './components/UploadZone';
import FileQueueItem from './components/FileQueueItem';
import UploadProgress from './components/UploadProgress';
import UploadSettings from './components/UploadSettings';
import ValidationAlert from './components/ValidationAlert';
import UploadDiagnosticPanel from './components/UploadDiagnosticPanel';
import { ToastContainer } from '../../components/ui/Toast';
import useToast from '../../hooks/useToast';
import { s3UploadService } from '../../services/s3UploadService';
import { bucketConfigService } from '../../services/bucketConfigService';

const UploadManager = () => {
  const navigate = useNavigate();
  const [isDragging, setIsDragging] = useState(false);
  const [fileQueue, setFileQueue] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [activeBucket, setActiveBucket] = useState(null);
  const [uploadStats, setUploadStats] = useState({
    totalFiles: 0,
    completedFiles: 0,
    failedFiles: 0,
    uploadingFiles: 0,
    overallProgress: 0,
    estimatedTime: 0,
    totalSpeed: null
  });
  const [settings, setSettings] = useState({
    imageQuality: 'high',
    destinationFolder: 'root',
    autoOrganize: true,
    generateThumbnails: true,
    overwriteExisting: false,
    preserveMetadata: true
  });
  const { toasts, showToast, removeToast } = useToast();
  const [diagnosticLogs, setDiagnosticLogs] = useState([]);
  const [isDiagnosticExpanded, setIsDiagnosticExpanded] = useState(false);
  const [s3ClientInitialized, setS3ClientInitialized] = useState(false);
  const [uploadCancelControllers, setUploadCancelControllers] = useState(new Map());

  const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
  const MAX_FILES = 50;
  const ALLOWED_TYPES = [
    'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml',
    'video/mp4', 'video/webm', 'video/quicktime',
    'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/zip', 'application/x-rar-compressed'
  ];
  const UPLOAD_TIMEOUT = 180000; // 3 minutes timeout for uploads
  const MAX_RETRY_ATTEMPTS = 3; // Maximum retry attempts per file

  // Load active bucket on mount
  useEffect(() => {
    loadActiveBucket();
  }, []);

  const addDiagnosticLog = (message, type = 'info', details = null) => {
    const timestamp = new Date()?.toLocaleTimeString();
    const newLog = {
      timestamp,
      message,
      type,
      details
    };
    
    setDiagnosticLogs(prev => [newLog, ...prev]?.slice(0, 100)); // Keep last 100 logs
    console.log(`[Upload Diagnostics] [${type?.toUpperCase()}] ${message}`, details || '');
  };

  const loadActiveBucket = async () => {
    try {
      console.log('[UploadManager] Loading active bucket configuration...');
      addDiagnosticLog('Loading bucket configuration...', 'info');
      
      const buckets = await bucketConfigService?.getAll();
      
      if (buckets?.length === 0) {
        console.warn('[UploadManager] No bucket configurations found');
        addDiagnosticLog('No bucket configurations found', 'warning');
        setValidationErrors(['No S3 bucket configured. Please configure a bucket in Settings.']);
        return;
      }

      // Use default bucket or first available
      const defaultBucket = buckets?.find(b => b?.isDefault) || buckets?.[0];
      setActiveBucket(defaultBucket);
      
      console.log('[UploadManager] Active bucket loaded:', {
        bucketName: defaultBucket?.bucketName,
        region: defaultBucket?.bucketRegion
      });

      addDiagnosticLog(`Bucket configured: ${defaultBucket?.bucketName}`, 'success', {
        bucketName: defaultBucket?.bucketName,
        region: defaultBucket?.bucketRegion
      });

      // Initialize S3 client
      await s3UploadService?.initializeClient(defaultBucket);
      setS3ClientInitialized(true);
      addDiagnosticLog('S3 client initialized successfully', 'success');
      
    } catch (error) {
      console.error('[UploadManager] Failed to load bucket:', error);
      addDiagnosticLog(`Failed to load bucket: ${error?.message}`, 'error', {
        error: error?.message,
        stack: error?.stack
      });
      setValidationErrors([`Failed to load bucket configuration: ${error?.message}`]);
    }
  };

  const validateFiles = (files) => {
    const errors = [];
    const validFiles = [];

    if (fileQueue?.length + files?.length > MAX_FILES) {
      errors?.push(`Maximum ${MAX_FILES} files allowed per batch. Current queue: ${fileQueue?.length}, Attempting to add: ${files?.length}`);
      return { validFiles: [], errors };
    }

    files?.forEach(file => {
      if (file?.size > MAX_FILE_SIZE) {
        errors?.push(`${file?.name}: File size exceeds 100MB limit (${(file?.size / 1024 / 1024)?.toFixed(2)}MB)`);
        return;
      }

      if (!ALLOWED_TYPES?.includes(file?.type)) {
        errors?.push(`${file?.name}: Unsupported file type (${file?.type || 'unknown'})`);
        return;
      }

      const duplicate = fileQueue?.find(f => f?.name === file?.name && f?.size === file?.size);
      if (duplicate) {
        errors?.push(`${file?.name}: Duplicate file already in queue`);
        return;
      }

      validFiles?.push({
        id: `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`,
        name: file?.name,
        size: file?.size,
        type: file?.type,
        status: 'pending',
        progress: 0,
        speed: null,
        error: null,
        file: file
      });
    });

    return { validFiles, errors };
  };

  const handleFileSelect = (files) => {
    const { validFiles, errors } = validateFiles(files);
    
    if (errors?.length > 0) {
      setValidationErrors(errors);
    }

    if (validFiles?.length > 0) {
      setFileQueue(prev => [...prev, ...validFiles]);
      updateUploadStats([...fileQueue, ...validFiles]);
    }
  };

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e?.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragging(false);
    const files = Array.from(e?.dataTransfer?.files);
    handleFileSelect(files);
  };

  const removeFile = (fileId) => {
    // Cancel upload if it's in progress
    const fileItem = fileQueue?.find(f => f?.id === fileId);
    if (fileItem?.status === 'uploading' && fileItem?.uploadId) {
      cancelFileUpload(fileItem?.uploadId);
    }
    
    const updatedQueue = fileQueue?.filter(f => f?.id !== fileId);
    setFileQueue(updatedQueue);
    updateUploadStats(updatedQueue);
  };

  const retryFile = (fileId) => {
    setFileQueue(prev => prev?.map(f => 
      f?.id === fileId ? { ...f, status: 'pending', progress: 0, error: null, retryCount: 0 } : f
    ));
  };

  const cancelFileUpload = (uploadId) => {
    try {
      addDiagnosticLog(`Canceling upload: ${uploadId}`, 'info');
      const canceled = s3UploadService?.cancelUpload(uploadId);
      
      if (canceled) {
        addDiagnosticLog(`✓ Upload canceled: ${uploadId}`, 'warning');
        showToast('Upload canceled', 'warning');
      }
    } catch (error) {
      addDiagnosticLog(`Failed to cancel upload: ${error?.message}`, 'error');
    }
  };

  const startUpload = async () => {
    const pendingFiles = fileQueue?.filter(f => f?.status === 'pending' || f?.status === 'error');
    
    if (pendingFiles?.length === 0) {
      console.warn('[UploadManager] No files to upload');
      addDiagnosticLog('No files to upload', 'warning');
      showToast('No files to upload', 'warning');
      return;
    }

    if (!activeBucket) {
      console.error('[UploadManager] No active bucket configured');
      addDiagnosticLog('No active bucket configured', 'error');
      setValidationErrors(['No S3 bucket configured. Please configure a bucket in Settings.']);
      showToast('No S3 bucket configured', 'error');
      return;
    }

    console.log('[UploadManager] Starting upload...', {
      fileCount: pendingFiles?.length,
      bucket: activeBucket?.bucketName,
      settings: settings
    });

    addDiagnosticLog(`Starting upload of ${pendingFiles?.length} files`, 'info', {
      fileCount: pendingFiles?.length,
      bucket: activeBucket?.bucketName,
      destination: settings?.destinationFolder,
      timeout: `${UPLOAD_TIMEOUT / 1000}s`,
      maxRetries: MAX_RETRY_ATTEMPTS,
      method: 'AWS SDK Upload class with real progress tracking'
    });

    setIsUploading(true);
    let corsErrorDetected = false;
    let successCount = 0;
    let failureCount = 0;

    // Process files sequentially for better error tracking
    for (const fileItem of pendingFiles) {
      // Check if upload should be stopped (e.g., CORS error detected)
      if (corsErrorDetected) {
        addDiagnosticLog('Skipping remaining uploads due to CORS error', 'warning', {
          fileName: fileItem?.name
        });
        break;
      }

      try {
        console.log(`[UploadManager] Uploading file: ${fileItem?.name}`);
        addDiagnosticLog(`📤 Uploading: ${fileItem?.name}`, 'info', {
          fileName: fileItem?.name,
          fileSize: `${(fileItem?.size / 1024 / 1024)?.toFixed(2)} MB`,
          fileType: fileItem?.type,
          timeout: `${UPLOAD_TIMEOUT / 1000}s`,
          maxRetries: MAX_RETRY_ATTEMPTS,
          method: 'Multipart upload with real-time progress'
        });
        
        // Generate upload ID for tracking
        const uploadId = `upload-${fileItem?.id}-${Date.now()}`;
        
        // Update status to uploading with upload ID
        setFileQueue(prev => prev?.map(f => 
          f?.id === fileItem?.id ? { 
            ...f, 
            status: 'uploading', 
            progress: 0,
            uploadId: uploadId,
            startTime: Date.now(),
            retryCount: (f?.retryCount || 0)
          } : f
        ));

        // Start upload with REAL progress tracking via callback
        const startTime = Date.now();

        const result = await s3UploadService?.uploadFileWithRetry(fileItem?.file, {
          destinationFolder: settings?.destinationFolder,
          preserveMetadata: settings?.preserveMetadata,
          uploadId: uploadId,
          timeout: UPLOAD_TIMEOUT,
          maxRetries: MAX_RETRY_ATTEMPTS,
          retryDelay: 2000,
          // Real-time progress callback from AWS SDK
          onProgress: (progressData) => {
            addDiagnosticLog(`📊 Upload progress: ${fileItem?.name}`, 'info', {
              progress: `${progressData?.progress}%`,
              speed: progressData?.speed,
              uploaded: `${(progressData?.uploadedBytes / 1024 / 1024)?.toFixed(2)} MB`,
              total: `${(progressData?.totalBytes / 1024 / 1024)?.toFixed(2)} MB`,
              isStalled: progressData?.isStalled
            });

            setFileQueue(prev => prev?.map(f => 
              f?.id === fileItem?.id ? { 
                ...f, 
                progress: progressData?.progress,
                speed: progressData?.speed,
                elapsed: progressData?.elapsed,
                isStalled: progressData?.isStalled
              } : f
            ));
          },
          // Stall detection callback
          onStallDetected: (stallData) => {
            addDiagnosticLog(`⚠️ Upload stalled: ${fileItem?.name}`, 'warning', {
              fileName: stallData?.fileName,
              stallDuration: `${(stallData?.stallDuration / 1000)?.toFixed(1)}s`,
              troubleshooting: 'Check network connection, CORS configuration, or try a smaller file'
            });
          }
        });

        console.log(`[UploadManager] Upload successful:`, result);
        addDiagnosticLog(`✅ Upload successful: ${fileItem?.name}`, 'success', {
          fileName: fileItem?.name,
          uploadTime: `${result?.uploadTime}ms`,
          uploadSpeed: `${result?.uploadSpeed?.toFixed(2)} MB/s`,
          fileUrl: result?.fileUrl,
          retries: fileItem?.retryCount || 0
        });

        successCount++;

        // Update file status to completed
        setFileQueue(prev => prev?.map(f => 
          f?.id === fileItem?.id ? { 
            ...f, 
            status: 'completed', 
            progress: 100,
            fileUrl: result?.fileUrl,
            uploadTime: result?.uploadTime,
            uploadId: null
          } : f
        ));

      } catch (error) {
        console.error(`[UploadManager] Upload failed for ${fileItem?.name}:`, error);
        failureCount++;
        
        // Check for cancellation
        if (error?.name === 'UploadCanceled') {
          addDiagnosticLog(`🛑 Upload canceled: ${fileItem?.name}`, 'warning', {
            fileName: fileItem?.name
          });
          
          setFileQueue(prev => prev?.map(f => 
            f?.id === fileItem?.id ? { 
              ...f, 
              status: 'canceled', 
              error: 'Upload was canceled',
              progress: 0,
              uploadId: null
            } : f
          ));
          
          continue; // Skip to next file
        }
        
        // Check for CORS errors
        if (error?.message === 'CORS_UPLOAD_ERROR' || error?.troubleshooting?.type === 'CORS_UPLOAD') {
          corsErrorDetected = true;
          console.error('[UploadManager] CORS ERROR DETECTED - Uploads are blocked!', {
            error: error?.troubleshooting,
            file: fileItem?.name,
            bucket: activeBucket?.bucketName
          });

          addDiagnosticLog(`🚨 CORS Error: ${fileItem?.name}`, 'error', {
            fileName: fileItem?.name,
            errorType: 'CORS_UPLOAD_ERROR',
            bucketName: activeBucket?.bucketName,
            troubleshooting: error?.troubleshooting
          });
          
          setFileQueue(prev => prev?.map(f => 
            f?.id === fileItem?.id ? { 
              ...f, 
              status: 'error', 
              error: 'CORS policy blocks uploads. Check bucket configuration.',
              progress: 0,
              corsError: true,
              uploadId: null
            } : f
          ));
          
          // Show validation error with CORS troubleshooting
          setValidationErrors([
            '🚨 CORS Configuration Error Detected!',
            '',
            `Your S3 bucket "${activeBucket?.bucketName}" needs proper CORS configuration to allow file uploads from the browser.`,
            '',
            '📋 Required CORS Configuration:',
            '1. Go to AWS Console → S3 → Your Bucket → Permissions → CORS',
            '2. Add this CORS policy:',
            '',
            'JSON CORS Policy:',
            '[',
            '  {',
            '    "AllowedOrigins": ["*"],',
            '    "AllowedMethods": ["GET", "PUT", "POST", "DELETE", "HEAD"],',
            '    "AllowedHeaders": ["*"],',
            '    "ExposeHeaders": ["ETag"],',
            '    "MaxAgeSeconds": 3000',
            '  }',
            ']',
            '',
            '💡 Key Points:',
            '- PUT and POST methods are required for uploads',
            '- AllowedOrigins should include your app domain',
            '- Save and wait 2-3 minutes for changes to take effect',
            '',
            `Current Error: ${error?.troubleshooting?.details?.errorMessage || error?.message}`
          ]);

          showToast('Upload blocked by CORS policy. Check diagnostics panel.', 'error', 8000);
          
          break; // Stop processing remaining files if CORS error
        }

        // Check for timeout errors
        const isTimeout = error?.message?.includes('timeout') || error?.message?.includes('Upload timeout');
        
        addDiagnosticLog(`❌ Upload failed: ${fileItem?.name}`, 'error', {
          fileName: fileItem?.name,
          errorMessage: error?.message,
          errorName: error?.name,
          isTimeout: isTimeout,
          retries: fileItem?.retryCount || 0
        });
        
        // Provide specific error message for timeouts
        let errorMsg = error?.message;
        if (isTimeout) {
          errorMsg = `Upload timeout after ${UPLOAD_TIMEOUT / 1000}s. File may be too large or network is slow. Try: 1) Check your internet connection 2) Verify CORS configuration 3) Try a smaller file`;
        }
        
        setFileQueue(prev => prev?.map(f => 
          f?.id === fileItem?.id ? { 
            ...f, 
            status: 'error', 
            error: errorMsg,
            progress: 0,
            isTimeout: isTimeout,
            uploadId: null
          } : f
        ));

        showToast(`Failed to upload ${fileItem?.name}`, 'error');
      }
    }

    setIsUploading(false);
    
    if (corsErrorDetected) {
      console.log('[UploadManager] Upload stopped due to CORS configuration issue');
      addDiagnosticLog('Upload stopped due to CORS configuration issue', 'error');
      showToast('Upload stopped: CORS configuration required', 'error', 8000);
    } else {
      console.log('[UploadManager] Upload batch completed');
      addDiagnosticLog(`Upload batch completed: ${successCount} successful, ${failureCount} failed`, 
        failureCount > 0 ? 'warning' : 'success',
        { successCount, failureCount }
      );

      // Show completion notification
      if (successCount > 0 && failureCount === 0) {
        showToast(`🎉 Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}!`, 'success');
      } else if (successCount > 0 && failureCount > 0) {
        showToast(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''}, ${failureCount} failed`, 'warning', 6000);
      } else if (failureCount > 0) {
        showToast(`Failed to upload ${failureCount} file${failureCount > 1 ? 's' : ''}`, 'error');
      }
    }
  };

  // Add function to cancel all uploads
  const cancelAllUploads = () => {
    addDiagnosticLog('Canceling all uploads...', 'info');
    const canceledCount = s3UploadService?.cancelAllUploads();
    
    if (canceledCount > 0) {
      setFileQueue(prev => prev?.map(f => 
        f?.status === 'uploading' ? {
          ...f,
          status: 'canceled',
          error: 'Upload was canceled',
          progress: 0,
          uploadId: null
        } : f
      ));
      
      addDiagnosticLog(`✓ Canceled ${canceledCount} uploads`, 'warning');
      showToast(`Canceled ${canceledCount} upload${canceledCount > 1 ? 's' : ''}`, 'warning');
    }
    
    setIsUploading(false);
  };

  const updateUploadStats = (queue) => {
    const total = queue?.length;
    const completed = queue?.filter(f => f?.status === 'completed')?.length;
    const failed = queue?.filter(f => f?.status === 'error')?.length;
    const uploading = queue?.filter(f => f?.status === 'uploading')?.length;
    
    const totalProgress = queue?.reduce((sum, f) => sum + f?.progress, 0);
    const overallProgress = total > 0 ? Math.round(totalProgress / total) : 0;

    const uploadingSpeeds = queue?.filter(f => f?.status === 'uploading' && f?.speed)?.map(f => parseFloat(f?.speed));
    
    const avgSpeed = uploadingSpeeds?.length > 0
      ? `${(uploadingSpeeds?.reduce((a, b) => a + b, 0) / uploadingSpeeds?.length)?.toFixed(1)} MB/s`
      : null;

    const remainingFiles = total - completed - failed;
    const estimatedTime = remainingFiles > 0 && avgSpeed ? remainingFiles * 5 : 0;

    setUploadStats({
      totalFiles: total,
      completedFiles: completed,
      failedFiles: failed,
      uploadingFiles: uploading,
      overallProgress,
      estimatedTime,
      totalSpeed: avgSpeed
    });
  };

  useEffect(() => {
    updateUploadStats(fileQueue);
  }, [fileQueue]);

  const clearCompleted = () => {
    const updatedQueue = fileQueue?.filter(f => f?.status !== 'completed');
    setFileQueue(updatedQueue);
    updateUploadStats(updatedQueue);
  };

  const clearAll = () => {
    setFileQueue([]);
    setValidationErrors([]);
    updateUploadStats([]);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <ToastContainer toasts={toasts} onRemove={removeToast} />
      <main className="main-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={() => navigate('/file-browser')}
                className="focus-ring rounded-md p-2 hover:bg-muted transition-colors"
              >
                <Icon name="ArrowLeft" size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-foreground">Upload Manager</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Upload files to your S3 bucket with progress tracking and validation
                </p>
              </div>
            </div>
          </div>

          {validationErrors?.length > 0 && (
            <div className="mb-6">
              <ValidationAlert 
                errors={validationErrors} 
                onDismiss={() => setValidationErrors([])} 
              />
            </div>
          )}

          {/* Diagnostic Panel */}
          <div className="mb-6">
            <UploadDiagnosticPanel
              activeBucket={activeBucket}
              uploadLogs={diagnosticLogs}
              s3ClientStatus={s3ClientInitialized}
              isExpanded={isDiagnosticExpanded}
              onToggle={() => setIsDiagnosticExpanded(!isDiagnosticExpanded)}
            />
          </div>

          {/* Debug Info Panel */}
          {activeBucket && (
            <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} className="text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="flex-1 text-sm">
                  <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">
                    Active S3 Bucket Configuration
                  </p>
                  <div className="text-blue-700 dark:text-blue-300 space-y-1">
                    <p><span className="font-medium">Bucket:</span> {activeBucket?.bucketName}</p>
                    <p><span className="font-medium">Region:</span> {activeBucket?.bucketRegion}</p>
                    <p><span className="font-medium">Destination:</span> {settings?.destinationFolder === 'root' ? 'Root folder' : settings?.destinationFolder}</p>
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                    💡 Check browser console (F12) for detailed upload logs and debugging information
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 space-y-6">
              <UploadZone
                onFileSelect={handleFileSelect}
                isDragging={isDragging}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              />

              {fileQueue?.length > 0 && (
                <UploadProgress
                  totalFiles={uploadStats?.totalFiles}
                  completedFiles={uploadStats?.completedFiles}
                  failedFiles={uploadStats?.failedFiles}
                  uploadingFiles={uploadStats?.uploadingFiles}
                  overallProgress={uploadStats?.overallProgress}
                  estimatedTime={uploadStats?.estimatedTime}
                  totalSpeed={uploadStats?.totalSpeed}
                />
              )}

              {fileQueue?.length > 0 && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">
                      Upload Queue ({fileQueue?.length})
                    </h3>
                    <div className="flex items-center gap-2">
                      {uploadStats?.completedFiles > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={clearCompleted}
                          iconName="CheckCheck"
                          iconPosition="left"
                        >
                          Clear Completed
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearAll}
                        iconName="Trash2"
                        iconPosition="left"
                      >
                        Clear All
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3 max-h-[600px] overflow-y-auto scrollbar-thin">
                    {fileQueue?.map(file => (
                      <FileQueueItem
                        key={file?.id}
                        file={file}
                        onRemove={removeFile}
                        onRetry={retryFile}
                        onCancel={file?.status === 'uploading' && file?.uploadId ? () => cancelFileUpload(file?.uploadId) : null}
                      />
                    ))}
                  </div>

                  <div className="flex items-center gap-3 mt-6 pt-6 border-t border-border">
                    <Button
                      variant="default"
                      fullWidth
                      onClick={startUpload}
                      disabled={isUploading || fileQueue?.filter(f => f?.status === 'pending' || f?.status === 'error')?.length === 0}
                      loading={isUploading}
                      iconName="Upload"
                      iconPosition="left"
                    >
                      {isUploading ? 'Uploading...' : `Upload ${fileQueue?.filter(f => f?.status === 'pending' || f?.status === 'error')?.length} Files`}
                    </Button>
                    {isUploading && (
                      <Button
                        variant="destructive"
                        onClick={cancelAllUploads}
                        iconName="X"
                        iconPosition="left"
                      >
                        Cancel All
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => navigate('/file-browser')}
                      disabled={isUploading}
                    >
                      {isUploading ? 'Close' : 'Cancel'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-1">
              <UploadSettings
                settings={settings}
                onSettingsChange={setSettings}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UploadManager;